import { IComment, ICommentSet } from '@/shared/IComment';
import { ApiService } from './ApiService';
import { StorageCacheService } from './StorageCacheService';
import { COMMENT_STATUS } from '@/shared/constants';
import { Model, Question, PanelModel, QuestionPanelDynamicModel } from 'survey-core';

export interface IQuestionPath {
  panelName?: string;
  panelIndex?: number;
  questionName: string;
}

export interface IDynamicPanelInfo {
  dynamicPanel: Question;
  panelIndex: number;
}

export interface IQuestionTagOptions {
  page?: string;
  question?: string;
  panelIndex?: number;
  nestedQuestionIndex?: number;
}

export class CommentsService {
  // Cache for panel-to-dynamic-panel mapping using panel ID as key
  private static panelCache = new Map<string, IDynamicPanelInfo>();

  /**
   * Find which dynamic panel (if any) a panel instance belongs to
   * Uses pre-built cache for performance
   */
  public static findDynamicPanelForPanel(panel: PanelModel): IDynamicPanelInfo | null {
    return this.panelCache.get(panel.id) || null;
  }

  /**
   * Get the hierarchical question ID for a question, handling nested questions in dynamic and regular panels
   * Returns format: "QuestionName", "PanelName.index.QuestionName", or "PanelName.QuestionName"
   */
  public static getQuestionId(question: Question): string {
    const parentElement = question.parent;
    
    if (parentElement?.getType() === 'panel') {
      const panel = parentElement as PanelModel;
      // Check if this panel belongs to a dynamic panel
      const dynamicPanelInfo = this.findDynamicPanelForPanel(panel);
      
      if (dynamicPanelInfo) {
        // Dynamic panel: PanelName.index.QuestionName
        return `${dynamicPanelInfo.dynamicPanel.name}.${dynamicPanelInfo.panelIndex}.${question.name}`;
      } else if (panel.name && this.questionNeedsComment(panel.title)) {
        // Regular numbered panel: PanelName.QuestionName
        return `${panel.name}.${question.name}`;
      }
    }
    
    // Top-level question
    return question.name;
  }

  /**
   * Initialize the dynamic panel cache by scanning all dynamic panels in the survey
   * Call this when loading a new survey to build the cache proactively
   */
  public static initPanelCache(survey: Model) {
    this.panelCache = new Map();
    
    // Build cache for all panel instances in all dynamic panels
    // Note: getAllQuestions() exists but is not in the public type definitions
    const allQuestions = survey.getAllQuestions() || [];
    for (const q of allQuestions) {
      if (q.getType() === 'paneldynamic') {
        const dynamicPanel = q as QuestionPanelDynamicModel;
        dynamicPanel.panels.forEach((panel: PanelModel, idx: number) => {
          this.panelCache.set(panel.id, { dynamicPanel: q, panelIndex: idx });
        });
      }
    }
  }

  public static async putCourseComment(comment: IComment) {
    const result: ICommentSet = await ApiService.fetch(`/api/course/${comment.courseId}/comments/${comment.id}`, 'PUT', comment);
    StorageCacheService.resetCache(`course`);
    return result;
  }

  public static async deleteCourseComment(comment: IComment) {
    const result: ICommentSet = await ApiService.fetch(`/api/course/${comment.courseId}/comments/${comment.id}`, 'DELETE');
    StorageCacheService.resetCache(`course`);
    return result;
  }

  public static questionNeedsComment(title: string) {
    return title.match(/^\d+/);
  }

  /**
   * Parse a question ID that may include dynamic panel path notation.
   * Formats supported:
   * - \"QuestionName\" (top-level question)
   * - \"PanelName\" (top-level panel)
   * - \"PanelName.0.QuestionName\" (nested question in dynamic panel instance)
   * - \"PanelName.QuestionName\" (nested question in regular panel)
   */
  public static parseQuestionId(questionId: string): IQuestionPath {
    // Split by dots to handle hierarchical paths
    const parts = questionId.split('.');
    
    if (parts.length === 3) {
      // Try to parse dynamic panel format: \"AssessmentPlan.0.AssessmentMethods\"
      const panelIndex = parseInt(parts[1]);
      if (!isNaN(panelIndex)) {
        return {
          panelName: parts[0],
          panelIndex: panelIndex,
          questionName: parts[2]
        };
      }
    }
    
    if (parts.length === 2) {
      // Regular panel format: \"KeyPrinciples.KnowledgeTriangle\"
      return {
        panelName: parts[0],
        panelIndex: undefined,
        questionName: parts[1]
      };
    }
    
    // Fallback for simple IDs
    return { questionName: questionId };
  }

  /**
   * Resolve a question or panel from the survey using a hierarchical path.
   * Handles top-level questions, nested questions in dynamic panels, and questions in regular panels.
   */
  public static getQuestionFromPath(survey: Model, questionId: string) {
    const path = this.parseQuestionId(questionId);
    
    if (path.panelName) {
      if (path.panelIndex !== undefined) {
        // Get nested question from dynamic panel instance (using dot notation)
        const dynamicPanel = survey.getQuestionByName(path.panelName);
        if (dynamicPanel?.panels?.[path.panelIndex]) {
          const question = dynamicPanel.panels[path.panelIndex].getQuestionByName(path.questionName);
          if (question) return question;
        }
        return null;
      } else {
        // Get nested question from regular panel - use direct question lookup
        return survey.getQuestionByName(path.questionName);
      }
    }
    
    // Top-level question or panel
    return survey.getQuestionByName(path.questionName) || 
           survey.getPanelByName(path.questionName);
  }

  public static getQuestionCommentCount(comments: ICommentSet | undefined, filter: (comment: IComment) => boolean) {
    let count = 0;
    if (comments) {
      for (const key in comments) {
        const comment = comments[key];
        if (filter(comment)) {
          ++count;
        }
      }
    }
    return count;
  }

  public static getCommentStatusText(status?: string) {
    switch (status) {
      case COMMENT_STATUS.ACTION_REQUIRED:
        return "Action Required";
      case COMMENT_STATUS.RESOLVED:
        return "Resolved";
      case COMMENT_STATUS.REOPENED:
        return "Reopened";
      default:
        return "Open";
    }
  }

  public static getCommentStatusColor(status?: string) {
    switch (status) {
      case COMMENT_STATUS.ACTION_REQUIRED:
        return "orange";
      case COMMENT_STATUS.RESOLVED:
        return "green";
      case COMMENT_STATUS.REOPENED:
        return "red";
      default:
        return "gray";
    }
  }

  /**
   * Get the index of a nested question within its parent panel
   */
  public static getNestedQuestionIndex(survey: Model, questionId: string): number | undefined {
    const path = this.parseQuestionId(questionId);
    if (!path.panelName) return undefined;
    
    const question = this.getQuestionFromPath(survey, questionId);
    if (!question) return undefined;
    
    let panelToSearch: PanelModel | Question | undefined;
    
    if (path.panelIndex !== undefined) {
      // Dynamic panel case - get as Question and cast to QuestionPanelDynamicModel
      const dynamicPanel = survey.getQuestionByName(path.panelName) as QuestionPanelDynamicModel;
      if (dynamicPanel?.getType() === 'paneldynamic') {
        panelToSearch = dynamicPanel.panels[path.panelIndex];
      }
    } else {
      // Regular panel case
      panelToSearch = survey.getPanelByName(path.panelName) || survey.getQuestionByName(path.panelName);
    }
    
    if (!panelToSearch) return undefined;
    
    const elements = (panelToSearch as PanelModel).elements;
    const index = elements?.findIndex((el) => el.name === question.name);
    return (index !== undefined && index >= 0) ? index : undefined;
  }

  /**
   * Get the display tag for a comment, handling nested questions
   */
  public static getCommentQuestionTag(comment: IComment, survey: Model): string {
    const path = comment.questionId ? this.parseQuestionId(comment.questionId) : undefined;
    const nestedQuestionIndex = comment.questionId ? this.getNestedQuestionIndex(survey, comment.questionId) : undefined;
    return this.getQuestionTag({
      page: comment.page,
      question: comment.question,
      panelIndex: path?.panelIndex,
      nestedQuestionIndex
    });
  }

  /**
   * Get the full question title for a comment, including parent panel context for nested questions
   */
  public static getQuestionTitleForComment(survey: Model, questionId: string): string | undefined {
    const question = this.getQuestionFromPath(survey, questionId);
    if (!question) return undefined;

    const path = this.parseQuestionId(questionId);
    if (!path.panelName) return question.processedTitle;
    
    const parentPanel = survey.getPanelByName(path.panelName) || survey.getQuestionByName(path.panelName);
    const nestedQuestionTitle = question.processedTitle || path.questionName;
    
    return parentPanel?.processedTitle 
      ? `${parentPanel.processedTitle} - ${nestedQuestionTitle}`
      : nestedQuestionTitle;
  }

  public static getQuestionTag(options: IQuestionTagOptions): string {
    const { page, question, panelIndex, nestedQuestionIndex } = options;
    const questionNum = question?.match(/^\d+\w*/)?.[0] ?? '';
    const panelSuffix = panelIndex !== undefined ? `.${panelIndex + 1}` : '';
    const nestedSuffix = nestedQuestionIndex !== undefined ? `.${nestedQuestionIndex + 1}` : '';
    return `${page ?? ''}${questionNum}${panelSuffix}${nestedSuffix}`;
  }

  public static getQuestions(commentSet: ICommentSet, moduleId?: string) {
    const questionSet: { [key: string]: { count: number, questionId: string, title: string, moduleId: string } } = {}
    for (const key in commentSet) {
      const comment = commentSet[key];
      if (comment.questionId && (!comment.moduleId || comment.moduleId === moduleId)) {
        const found = questionSet[comment.questionId ?? ''];
        if (found) {
          found.count = found.count + 1;
        } else {
          // Extract panel index if present in questionId
          const path = this.parseQuestionId(comment.questionId);
          const panelSuffix = path.panelIndex !== undefined ? ` [${path.panelIndex + 1}]` : '';
          
          questionSet[comment.questionId ?? ''] = {
            count: 1,
            questionId: comment.questionId,
            title: `${comment.page}${comment.question}${panelSuffix}`,
            moduleId: comment.moduleId
          };
        }
      }
    }
    const questions = Object
      .keys(questionSet)
      .map(key => questionSet[key])
      .sort((a, b) => Intl.Collator().compare(a.moduleId, b.moduleId));
    return questions;
  }

  public static compareComment(a: IComment, b: IComment) {
    const aPath = a.questionId ? CommentsService.parseQuestionId(a.questionId) : undefined;
    const bPath = b.questionId ? CommentsService.parseQuestionId(b.questionId) : undefined;
    const aQuestion = CommentsService.getQuestionTag({ page: a.page, question: a.question, panelIndex: aPath?.panelIndex });
    const bQuestion = CommentsService.getQuestionTag({ page: b.page, question: b.question, panelIndex: bPath?.panelIndex });
    if (aQuestion && !bQuestion) return +1;
    if (!aQuestion && bQuestion) return -1;
    return Intl.Collator().compare(aQuestion, bQuestion);
  }

  public static compareTimestamp(a: IComment, b: IComment) {
    if (a.timestamp && !b.timestamp) return -1;
    if (!a.timestamp && b.timestamp) return +1;
    return a.timestamp > b.timestamp ? +1 : -1
  }

}